import React, { useRef, useState } from "react";
import { Upload, X, Loader2, Video as VideoIcon, ImageIcon, Camera } from "lucide-react";
import api from "@/lib/api";

/**
 * Media picker for stories — accepts image OR short video (≤ 15 s).
 *
 * Value shape: { url: string, media_type: 'image' | 'video' } (or null)
 * onChange({ url, media_type }) — cleared with null.
 *
 * On mobile, the file input offers Camera + Gallery natively.
 */
const MAX_VIDEO_SECONDS = 15;
const MAX_VIDEO_MB = 25;

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1080;
        let w = img.width;
        let h = img.height;
        const ratio = Math.min(max / w, max / h, 1);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        let q = 0.85;
        const toBlob = (qq) => new Promise((r) => canvas.toBlob(r, "image/jpeg", qq));
        (async () => {
          let blob = await toBlob(q);
          while (blob && blob.size > 500 * 1024 && q > 0.4) {
            q -= 0.1;
            blob = await toBlob(q);
          }
          if (!blob) return reject(new Error("compress failed"));
          resolve(blob);
        })();
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const probeVideo = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({ duration: v.duration, width: v.videoWidth, height: v.videoHeight });
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No pudimos leer el video."));
    };
    v.src = url;
  });

const StoryMediaUpload = ({ value, onChange, testid = "story-media" }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (!isVideo && !isImage) {
      setError("Elige una foto o un video corto.");
      return;
    }
    setLoading(true);
    try {
      if (isVideo) {
        if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
          throw new Error(`Video muy grande. Máx ${MAX_VIDEO_MB} MB.`);
        }
        const meta = await probeVideo(file);
        if (meta.duration > MAX_VIDEO_SECONDS + 0.5) {
          throw new Error(`El video dura ${Math.round(meta.duration)} s. Máximo ${MAX_VIDEO_SECONDS} s.`);
        }
        const fd = new FormData();
        fd.append("file", file, file.name || "clip.mp4");
        const { data } = await api.post("/uploads/video?purpose=story", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onChange({ url: data.url, media_type: "video" });
      } else {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error("Imagen muy grande. Máx 5 MB.");
        }
        const blob = await compressImage(file);
        const fd = new FormData();
        fd.append("file", blob, "story.jpg");
        const { data } = await api.post("/uploads/image?purpose=story", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onChange({ url: data.url, media_type: "image" });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "No se pudo subir.";
      setError(msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`${testid}-wrapper`}>
      <label className="text-xs font-bold uppercase tracking-wider text-teal-soft block mb-1">
        Contenido de la historia
      </label>
      <p className="text-xs text-teal-soft mb-2">
        Foto (JPG/PNG · máx 5 MB) o video corto (MP4/MOV · máx {MAX_VIDEO_SECONDS} s · {MAX_VIDEO_MB} MB)
      </p>

      <div
        onClick={() => !loading && inputRef.current?.click()}
        className="aspect-square w-full bg-cream rounded-2xl border-2 border-dashed border-gray-300 hover:border-orange transition-colors cursor-pointer overflow-hidden flex items-center justify-center relative"
        data-testid={`${testid}-dropzone`}
      >
        {loading ? (
          <div className="text-teal-soft text-center p-3 text-xs flex flex-col items-center gap-1">
            <Loader2 size={22} className="animate-spin" />
            <span>Subiendo…</span>
          </div>
        ) : value?.url ? (
          <>
            {value.media_type === "video" ? (
              <video
                src={value.url}
                className="w-full h-full object-cover"
                muted
                playsInline
                autoPlay
                loop
                data-testid={`${testid}-preview-video`}
              />
            ) : (
              <img src={value.url} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview-image`} />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center shadow"
              aria-label="Quitar"
              data-testid={`${testid}-remove`}
            >
              <X size={16} />
            </button>
            {value.media_type === "video" && (
              <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">
                <VideoIcon size={11} /> Video
              </span>
            )}
          </>
        ) : (
          <div className="text-teal-soft text-center p-3">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Camera size={18} />
              <span className="text-gray-300">|</span>
              <ImageIcon size={18} />
              <span className="text-gray-300">|</span>
              <VideoIcon size={18} />
            </div>
            <span className="text-xs font-semibold block">Subir foto o video</span>
            <span className="block text-[10px] text-gray-400 mt-0.5">
              Cámara o galería
            </span>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/mp4,video/quicktime,video/webm,video/3gpp"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid={testid}
      />

      {error && (
        <p className="text-xs text-red-600 mt-2" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default StoryMediaUpload;
